import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { FormattedMessage, intlShape } from 'react-intl';
import { Map as IMap, List as IList } from 'immutable';
import Markdown from 'markdown-to-jsx';
import { InnerPrimaryButton } from 'cstar-react-primitives/lib/buttons/PrimaryButton';
import { RadioGroup } from 'cstar-react-primitives/lib/microFields/RadioGroup';
import { Icon } from 'cstar-react-primitives/lib/icons/IconDisplay';
import { errorMsgAttributes } from 'cstar-react-primitives/lib/microFields/fieldAttrsUtils';

import { Alert } from 'cstar-react-primitives/lib/redux-form/alert/Alert';
import { ScrollToTop } from '../primitive/ScrollToTopWrap';
import DenomRange from '../denomination/DenomRange';
import { getIntlCurrencyCode } from '../intl/intlSelectors';
import {
    getBrandCatalogs,
    getBrandCode
} from '../brand/brandSelectors';
import ScrollIntoView from '../primitive/ScrollIntoViewWrap';
import windowSize from '../utils/windowSize';
import { isMobileView } from '../utils/mobileUtils';
import { getStaticMediaURL } from '../app/bootstrap';
import { getPreferredClassicGift } from '../routing/flowSelectors';

import programMessages from './programMessages';
import { formatProgramMessage } from './formatProgramMessage';
import {
    getSelectableProgramsInfo,
    getClassicCardDescription,
    getCustomCardMessages,
    getDefaultFaceplateForProgram,
    DEFAULT_ELECTRONIC_FACEPLATES,
    DEFAULT_PHYSICAL_FACEPLATES,
    getAlternateImageForProgram,
    getShowCurrencyCodeOnProgramTiles,
    getShowCurrencyListOnProgramTiles,
    getExcludedBillingStatesForProgram,
    getIncludedBillingStatesForProgram
} from './programListSelectors';
import { changeProgram, PICKER_CLASS } from './programModule';
import { CustomCard } from './CustomCard';
import ProgramPreview from './ProgramPreview';
import {
    generateProgramProps,
    generateSelectProgramBtnProps
} from './programUtils';
import stateMap from '../addressFields/data/USStateMap';



export const CUSTOM_TILE_VALUE = -1;

export const ProgramRadio = ({
    intl,
    name,
    text,
    description,
    classicCardsDescription,
    showClassicCardsDescription,
    previewFaceplates,
    minRange,
    maxRange,
    rangeCurrencyCode,
    currencyList,
    showDenomRange,
    brandCode,
    previewImageFilename,
    previewImageAltText,
    showCurrencyCode,
    showCurrencyList,
    excludedBillingStates,
    includedBillingStates
}) => {
    const denomRangeProps = {
        minRange,
        maxRange,
        rangeCurrencyCode,
        currencyList,
        showCurrencyCode,
        showCurrencyList
    };
    const previewProps = {
        defaultFaceplates: previewFaceplates
    };

    const showAltImage = previewImageFilename && previewImageAltText;
    const staticUrl = getStaticMediaURL(`/images/${brandCode}/${previewImageFilename}`);
    const includedStates = includedBillingStates && includedBillingStates.replace(/ /g, '').split(',');
    const count = includedStates.length;
    const message = programMessages.programStateExclusionWarning;
    message.values = {
        programName: name,
        count,
        state: count === 1 && intl.formatMessage(stateMap[includedStates.shift()])
    };

    const renderCardHead = () => (
        <div className='card-head'>
            { !showAltImage && <ProgramPreview {...previewProps} /> }
            <span className="card-title">{text}</span>
            { showDenomRange && <DenomRange {...denomRangeProps} /> }
        </div>
    );

    return (
        <Fragment>
            { (excludedBillingStates.trim().length > 0)
                && <div className='picker-alert'><Alert alertType='info' hasHeading={false} message={message} /></div>}
            { showAltImage && <img className='card-image' src={staticUrl} alt={previewImageAltText} /> }
            { renderCardHead() }
            { description && <span className="card-description"><Markdown>{description}</Markdown></span> }
            { showClassicCardsDescription
                && <span className="classic-description"><Markdown>{classicCardsDescription}</Markdown></span> }
        </Fragment>
    );
};

ProgramRadio.defaultProps = {
    description: null,
    classicCardsDescription: '',
    previewFaceplates: IList(),
    minRange: 0,
    maxRange: 0,
    showDenomRange: false,
    showClassicCardsDescription: false,
    previewImageFilename: null,
    previewImageAltText: null,
    excludedBillingStates: '',
    includedBillingStates: ''
};

ProgramRadio.propTypes = {
    intl: intlShape.isRequired,
    name: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    description: PropTypes.string,
    classicCardsDescription: PropTypes.string,
    previewFaceplates: PropTypes.instanceOf(IList),
    minRange: PropTypes.number,
    maxRange: PropTypes.number,
    rangeCurrencyCode: PropTypes.string.isRequired,
    currencyList: PropTypes.instanceOf(IList).isRequired,
    showDenomRange: PropTypes.bool,
    showClassicCardsDescription: PropTypes.bool,
    brandCode: PropTypes.string.isRequired,
    previewImageFilename: PropTypes.string,
    previewImageAltText: PropTypes.string,
    showCurrencyCode: PropTypes.bool.isRequired,
    showCurrencyList: PropTypes.bool.isRequired,
    excludedBillingStates: PropTypes.string,
    includedBillingStates: PropTypes.string
};



const getRadioOptions = (
    programsInfo,
    programProps,
    customCardMessages,
    customTileSelected,
    brandCode,
    windowWidth
) => {
    const radioOptions = programsInfo.get('programs').map((group, index) => {
        const { programCode, ...programRadioProps } = generateProgramProps(group, index, programProps);
        return {
            value: programCode,
            children: (<ProgramRadioWrapper {...programRadioProps} />)
        };
    }).toJS();

    const showCustomTile = Boolean(
        customCardMessages.get('customCardTitle') && customCardMessages.get('customCardActionUrl')
    );
    if (showCustomTile) {
        const customTileProps = {
            text: customCardMessages.get('customCardTitle', ''),
            description: customCardMessages.get('customCardDescription', ''),
            header: customCardMessages.get('customCardHeader', ''),
            imgUrl: customCardMessages.get('customCardImage', ''),
            imgAlt: customCardMessages.get('customCardImageAlt', ''),
            brandCode
        };
        const customTileScrollProps = {
            doScroll: customTileSelected && isMobileView(windowWidth)
        };

        radioOptions.push({
            value: CUSTOM_TILE_VALUE,
            children: (
                <ScrollIntoView {...customTileScrollProps}>
                    <CustomCard {...customTileProps} />
                </ScrollIntoView>
            )
        });
    }

    return radioOptions;
};



export const getCartConflictMessage = (
    programsInfo,
    intl
) => {
    const conflictingCartProgramCode = programsInfo.get('conflictingCartProgramCode');
    const isReducedByBillingStateConflicts = programsInfo.get('isReducedByBillingStateConflicts');
    const programs = programsInfo.get('programs');
    if (programs && isReducedByBillingStateConflicts && conflictingCartProgramCode) {
        const conflictingCartProgram = (
            programs.find(program => program.get('programCode') === conflictingCartProgramCode)
        );
        if (conflictingCartProgram) {
            const conflictingCartProgramName = formatProgramMessage(
                intl,
                programMessages.programTitle,
                conflictingCartProgramCode,
                { programCode: conflictingCartProgramCode }
            );
            return intl.formatMessage(programMessages.billingStateCartConflictWarning, {
                programName: conflictingCartProgramName
            });
        }
    }
    return undefined;
};


const ProgramRadioWrapper = (
    programProps
) => {
    const {
        shouldFocus,
        isChecked,
        windowWidth,
        index
    } = programProps;

    if ((index === 0) && shouldFocus) {
        programProps.doFocus = shouldFocus;
    }
    const programScrollProps = {
        doScroll: isChecked && isMobileView(windowWidth)
    };
    return (
        <ScrollIntoView {...programScrollProps}>
            <ProgramRadio {...programProps} />
        </ScrollIntoView>
    );
};


export class ProgramPicker extends React.Component {
    constructor(props) {
        super(props);
        const { preferredClassicGift, programsInfo } = props;

        this.state = {
            selectedProgram: (preferredClassicGift
                && programsInfo.get('programs').some(program => program.get('programCode') === '')) ? '' : null,
            showValidationWarning: false,
            customTileSelected: false
        };

        this.showValidationWarning = this.showValidationWarning.bind(this);
        this.hideValidationWarning = this.hideValidationWarning.bind(this);
        this.selectProgram = this.selectProgram.bind(this);
    }

    hideValidationWarning() {
        this.setState({ showValidationWarning: false });
    }

    showValidationWarning() {
        this.setState({ showValidationWarning: true });
    }

    selectProgram(programCode) {
        if (programCode === CUSTOM_TILE_VALUE) {
            this.setState({ selectedProgram: null, customTileSelected: true });
        } else {
            this.setState({ selectedProgram: programCode, customTileSelected: false });
        }

        this.hideValidationWarning();
    }

    render() {
        const { selectedProgram, showValidationWarning, customTileSelected } = this.state;
        const {
            intl,
            cashbotName,
            name,
            programsInfo,
            shouldFocus,
            switchProgram,
            submitAction,
            classicCardsDescription,
            getFaceplateByProgram,
            classicCatalogs,
            defaultCurrencyCode,
            customCardMessages,
            brandCode,
            windowWidth,
            getAltImageByProgram,
            showCurrencyCode,
            showCurrencyList,
            preferredClassicGift,
            getExcludedBillingStates,
            getIncludedBillingStates
        } = this.props;

        const selectProgramBtnProps = generateSelectProgramBtnProps({
            selectedProgram,
            customTileSelected,
            customCardMessages,
            switchProgram,
            submitAction,
            windowWidth,
            intl,
            showValidationWarning: this.showValidationWarning
        });

        const programProps = {
            shouldFocus,
            intl,
            getFaceplateByProgram,
            classicCatalogs,
            defaultCurrencyCode,
            classicCardsDescription,
            name,
            selectedProgram,
            windowWidth,
            DEFAULT_PHYSICAL_FACEPLATES,
            DEFAULT_ELECTRONIC_FACEPLATES,
            getAltImageByProgram,
            brandCode,
            showCurrencyCode,
            showCurrencyList,
            getExcludedBillingStates,
            getIncludedBillingStates
        };

        const radioOptions = getRadioOptions(
            programsInfo,
            programProps,
            customCardMessages,
            customTileSelected,
            brandCode,
            windowWidth
        );

        const isReducedByBillingStateConflicts = programsInfo.get('isReducedByBillingStateConflicts');
        const conflictingCartProgramCode = programsInfo.get('conflictingCartProgramCode');
        const cartConflictMessage = getCartConflictMessage(programsInfo, intl);

        const radioGroupProps = {
            name,
            cashbotName,
            legend: intl.formatMessage(programMessages.programLegend),
            styleType: 'checkedpicker',
            containerClass: PICKER_CLASS,
            radioButtonClass: `${PICKER_CLASS}--cell`,
            checkmarkClass: `${PICKER_CLASS}__check`,
            radioOptions,
            defaultValue: (preferredClassicGift && radioOptions.some(option => option.value === '')) ? '' : null,
            handleChange: this.selectProgram,
            doFocus: shouldFocus
        };

        return (
            <ScrollToTop doScroll={showValidationWarning}>
                { isReducedByBillingStateConflicts && conflictingCartProgramCode && (
                    <div className='picker-alert'>
                        <Alert alertType='info' hasHeading={false} message={cartConflictMessage} />
                    </div>
                ) }
                <div {...errorMsgAttributes({ id: 'program-picker' })}>
                    { showValidationWarning && (
                        <Fragment>
                            <Icon type="error" />
                            <FormattedMessage {...programMessages.noProgramSelectedWarning} />
                        </Fragment>
                    )}
                </div>

                <RadioGroup {...radioGroupProps} />

                <InnerPrimaryButton {...selectProgramBtnProps} />
            </ScrollToTop>
        );
    }
}

ProgramPicker.defaultProps = {
    shouldFocus: true,
    classicCardsDescription: '',
    customCardMessages: IMap(),
    preferredClassicGift: false
};

ProgramPicker.propTypes = {
    intl: intlShape.isRequired,
    cashbotName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    programsInfo: ImmutablePropTypes.map.isRequired,
    shouldFocus: PropTypes.bool,
    switchProgram: PropTypes.func.isRequired,
    submitAction: PropTypes.func.isRequired,
    classicCardsDescription: PropTypes.string,
    getFaceplateByProgram: PropTypes.func.isRequired,
    classicCatalogs: PropTypes.objectOf(IMap).isRequired,
    customCardMessages: PropTypes.instanceOf(IMap),
    brandCode: PropTypes.string.isRequired,
    defaultCurrencyCode: PropTypes.string.isRequired,
    getAltImageByProgram: PropTypes.func.isRequired,
    showCurrencyCode: PropTypes.bool.isRequired,
    showCurrencyList: PropTypes.bool.isRequired,
    preferredClassicGift: PropTypes.bool,
    
    windowWidth: PropTypes.number.isRequired,
    getExcludedBillingStates: PropTypes.func.isRequired,
    getIncludedBillingStates: PropTypes.func.isRequired
};

export const mapStateToProps = state => ({
    programsInfo: getSelectableProgramsInfo(state),
    classicCardsDescription: getClassicCardDescription(state),
    getFaceplateByProgram: getDefaultFaceplateForProgram(state),
    classicCatalogs: getBrandCatalogs(state),
    defaultCurrencyCode: getIntlCurrencyCode(state),
    customCardMessages: getCustomCardMessages(state),
    brandCode: getBrandCode(state),
    getAltImageByProgram: getAlternateImageForProgram(state),
    showCurrencyCode: getShowCurrencyCodeOnProgramTiles(state),
    showCurrencyList: getShowCurrencyListOnProgramTiles(state),
    preferredClassicGift: getPreferredClassicGift(state),
    getExcludedBillingStates: getExcludedBillingStatesForProgram(state),
    getIncludedBillingStates: getIncludedBillingStatesForProgram(state)
});

export const mapDispatchToProps = {
    switchProgram: changeProgram
};

export default connect(mapStateToProps, mapDispatchToProps)(windowSize(ProgramPicker));
